/**
 * v1.2d — Balance Top-up Form MCP App
 *
 * Enterprise-only inline form for adjusting account balances via
 * `modify_account_balance`. Supports preview-only mode before committing.
 * Scoped to `admin` — pairs with MFA gate from PR #46.
 */

import { z } from "zod";
import type { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import {
  registerAppTool,
  registerAppResource,
  RESOURCE_MIME_TYPE,
} from "@modelcontextprotocol/ext-apps/server";
import { OcsClient } from "../client.js";
import type { ToolContext } from "../tools.js";
import { wrapHandler, TOOL_SCOPES } from "../tools.js";

async function safeCallWithToken<T = Record<string, unknown>>(
  client: OcsClient,
  _token: string,
  method: string,
  params: Record<string, unknown> | number | string = {},
): Promise<{ data: T | null; error: string | null }> {
  try {
    return { data: await client.call<T>(method, params), error: null };
  } catch (err) {
    return {
      data: null,
      error: err instanceof Error ? err.message : String(err),
    };
  }
}

export interface BalanceTopupStructuredContent {
  iccid: string;
  delta: number;
  current_balance?: number;
  new_balance?: number;
  preview: boolean;
  success?: boolean;
}

export function registerBalanceTopupApp(
  server: McpServer,
  ctx: ToolContext,
): void {
  // Tier guard — enterprise only
  if (ctx.props.tier !== "enterprise") return;

  // ── Resource ─────────────────────────────────────────────────────────────
  registerAppResource(
    server,
    "Balance Top-up Form",
    "ui://balance-topup-form",
    {
      description:
        "Admin balance top-up form — enter an ICCID and delta amount to preview and commit account balance adjustments. Enterprise only. Requires recent MFA verification.",
    },
    async () => {
      let html: string;
      try {
        const resp = await ctx.env.ASSETS.fetch(
          new Request("https://internal/views/balance-topup/index.html"),
        );
        html = await resp.text();
      } catch {
        html = "<html><body><p>Balance top-up form unavailable.</p></body></html>";
      }
      return {
        contents: [
          {
            uri: "ui://balance-topup-form",
            mimeType: RESOURCE_MIME_TYPE,
            text: html,
          },
        ],
      };
    },
  );

  // ── Tool ─────────────────────────────────────────────────────────────────
  registerAppTool(
    server,
    "balance_topup_form",
    {
      title: "Balance Top-up Form",
      description:
        "Adjust the OCS reseller-account balance behind one subscriber, through a form panel the user can review before it commits. " +
        "Defaults to preview: a call that omits `preview` returns a projection and writes nothing. Only `preview: false` performs the OCS write. " +
        "Params: `iccid` (subscriber identifier used to find the account), `delta` (amount to add when positive, deduct when negative), " +
        "`preview` (boolean, default true). Requires `admin` scope plus recent MFA, and a client that can render UI panels. " +
        "Do NOT use this in a headless or scripted context — call `modify_account_balance` directly, which takes an `accountId` and needs no panel. " +
        "Do NOT use this for a subscriber's own balance — the `iccid` here only locates the account; per-subscriber money is `modify_subscriber_balance`. " +
        "Do NOT use this for the Carrier prepaid wallet or MCP credits — see `wallet_topup_checkout` and `credit_balance`.",
      inputSchema: {
        iccid: z.string().describe("The subscriber ICCID for the account lookup"),
        delta: z
          .number()
          .describe("Amount to add (positive) or deduct (negative) from the account balance"),
        preview: z
          .boolean()
          .default(true)
          .describe("If true, return preview without writing. If false, execute the balance change."),
      },
      annotations: { destructiveHint: true },
      _meta: {
        ui: {
          resourceUri: "ui://balance-topup-form",
          visibility: ["model", "app"] as ["model", "app"],
        },
      },
    },
    wrapHandler(
      "modify_account_balance",
      "modifyAccountBalance",
      TOOL_SCOPES["modify_account_balance"]!,
      ctx,
      async ({ iccid, delta, preview }: { iccid: string; delta: number; preview?: boolean }) => {
        const token = await ctx.getUserToken(ctx.props.sub);
        const client = new OcsClient(ctx.env.CARRIER_OCS_BASE_URL, token);

        if (preview === true) {
          // Preview path — fetch current subscriber balance, return projection
          const result = await safeCallWithToken<Record<string, unknown>>(
            client,
            token,
            "getSingleSubscriber",
            { iccid },
          );

          const currentBalance = result.data
            ? Number(result.data["balance"] ?? result.data["accountBalance"] ?? 0)
            : 0;
          const newBalance = currentBalance + delta;

          const structured: BalanceTopupStructuredContent = {
            iccid,
            delta,
            current_balance: currentBalance,
            new_balance: newBalance,
            preview: true,
          };

          return {
            content: [
              {
                type: "text" as const,
                text: `[Preview] ICCID: ${iccid} | Current balance: ${currentBalance} | Delta: ${delta >= 0 ? "+" : ""}${delta} | New balance: ${newBalance}. No changes made.`,
              },
            ],
            structuredContent: structured,
          };
        }

        // Execute path — call modify_account_balance via OCS
        const execResult = await safeCallWithToken<Record<string, unknown>>(
          client,
          token,
          // CAR-78: subscriber top-up uses modifySubscriberBalance with { subscriber, amount }
          // (delta). modifyAccountBalance is account-level and expects { accountId, amount, mode }.
          "modifySubscriberBalance",
          { subscriber: iccid, amount: delta },
        );

        if (execResult.error) {
          return {
            isError: true,
            content: [{ type: "text" as const, text: `Error: ${execResult.error}` }],
          };
        }

        const newBalance = execResult.data
          ? Number(execResult.data["balance"] ?? execResult.data["newBalance"] ?? 0)
          : delta;

        const structured: BalanceTopupStructuredContent = {
          iccid,
          delta,
          new_balance: newBalance,
          preview: false,
          success: true,
        };

        return {
          content: [{ type: "text" as const, text: "Balance updated." }],
          structuredContent: structured,
        };
      },
    ),
  );
}
